/**
 * Serializes filters to a URL-friendly string format
 * Format: field:operator:value(s) separated by |
 * Examples:
 *   - datePaid:after:2023-01-01
 *   - datePaid:between:2023-01-01,2023-12-31
 *   - amountCentsPaid:gte:10000
 *   - paymentMethod:is:cash
 *   - productIds:any:1,2,3 (URL-encoded for safety)
 *
 * @param {Array} filters - Array of filter objects
 * @returns {string} - Serialized filter string
 */
export function serializeFilters(filters) {
  if (!filters || filters.length === 0) return '';

  const serialized = filters
    .filter(filter => filter.field) // Only require field, operator is optional
    .map(filter => {
      // If no operator, just serialize the field
      if (!filter.operator) {
        return filter.field;
      }

      const parts = [filter.field, filter.operator];

      // Handle different filter types
      if (filter.operator === 'between') {
        // Range filter (date or currency)
        const from = filter.from !== null && filter.from !== undefined ? filter.from : '';
        const to = filter.to !== null && filter.to !== undefined ? filter.to : '';
        parts.push(`${from},${to}`);
      } else if (filter.operator === 'after' || filter.operator === 'gte') {
        // Greater than filter
        const from = filter.from !== null && filter.from !== undefined ? filter.from : '';
        parts.push(from);
      } else if (filter.operator === 'before' || filter.operator === 'lte') {
        // Less than filter
        const to = filter.to !== null && filter.to !== undefined ? filter.to : '';
        parts.push(to);
      } else if (filter.operator === 'is') {
        // Select filter
        const value = filter.value !== null && filter.value !== undefined ? filter.value : '';
        parts.push(value);
      } else if (filter.operator === 'any' || filter.operator === 'all') {
        // Multiselect filter - URL encode the values for safety
        const values = filter.values && filter.values.length > 0 ? filter.values.join(',') : '';
        parts.push(encodeURIComponent(values));
      }

      return parts.join(':');
    })
    .filter(str => str.length > 0);

  return serialized.join('|');
}

/**
 * Deserializes a URL filter string to filter objects
 *
 * @param {string} filterString - Serialized filter string
 * @returns {Array} - Array of filter objects
 */
export function deserializeFilters(filterString) {
  if (!filterString || typeof filterString !== 'string') return [];

  try {
    const filters = filterString.split('|').map(filterStr => {
      const parts = filterStr.split(':');

      // If only 1 part, it's just a field with no operator
      if (parts.length === 1) {
        return {
          id: Date.now() + Math.random(),
          field: parts[0],
          operator: '',
          from: null,
          to: null
        };
      }

      if (parts.length < 3) return null;

      const field = parts[0];
      const operator = parts[1];
      const valueStr = parts.slice(2).join(':'); // Join everything after operator in case value contains colons
      const filter = {
        id: Date.now() + Math.random(),
        field,
        operator
      };

      // Handle different filter types
      if (operator === 'between') {
        // Range filter
        const [from, to] = valueStr.split(',');
        filter.from = from || null;
        filter.to = to || null;
      } else if (operator === 'after' || operator === 'gte') {
        // Greater than filter
        filter.from = valueStr || null;
        filter.to = null;
      } else if (operator === 'before' || operator === 'lte') {
        // Less than filter
        filter.from = null;
        filter.to = valueStr || null;
      } else if (operator === 'is') {
        // Select filter - try to parse as number if it's numeric
        const value = valueStr || null;
        if (value && /^\d+$/.test(value)) {
          filter.value = parseInt(value, 10);
        } else {
          filter.value = value;
        }
      } else if (operator === 'any' || operator === 'all') {
        // Multiselect filter - URL decode first, then parse as numbers if all values are numeric
        const decodedValueStr = valueStr ? decodeURIComponent(valueStr) : '';
        const values = decodedValueStr ? decodedValueStr.split(',') : [];
        filter.values = values.map(v => {
          if (/^\d+$/.test(v)) {
            return parseInt(v, 10);
          }
          return v;
        });
      }

      return filter;
    }).filter(filter => filter !== null);

    return filters;
  } catch (err) {
    console.error('Failed to deserialize filters:', err);
    return [];
  }
}
